import { Route, RouteConfig } from "vue-router";

export const patientsRoutes: Array<RouteConfig> = [
  {
    path: "/patients",
    name: "patients",
    meta: {
      breadcrumb: [
        {
          text: "Patients",
        },
      ],
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/PatientsView.vue"
      ),
  },
  {
    path: "/patients/:anonymousCode",
    name: "singlePatientByCode",
    meta: {
      breadcrumb(route: Route) {
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: route.params.anonymousCode,
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/SinglePatientView.vue"
      ),
  },
  {
    path: "/patients/:id",
    name: "singlePatient",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/SinglePatientView.vue"
      ),
  },
  {
    path: "/patients/:patientId/screening/:activityId",
    name: "screening-view",
    props: true,
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.patientId;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: "Screening",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/ScreeningT0View.vue"
      ),
  },
  {
    path: "/patients/:id/documents/add",
    name: "addDocument",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.id;
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: "Documents",
          },
          {
            text: "Add",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/AddDocumentView.vue"
      ),
  },
  {
    path: "/patients/:id/documents/:documentId/edit",
    name: "documentEdit",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.id;
        const documentTitle = route.query.documentTitle;
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: "Documents",
          },
          {
            text: documentTitle,
          },
          {
            text: "Edit",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/AddDocumentView.vue"
      ),
  },
  {
    path: "/patients/:id/activityPlan/add",
    name: "addActivityPlan",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.id;
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: "ActivityPlan",
          },
          {
            text: "Add",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/AddActivityPlanView.vue"
      ),
  },
  {
    path: "/patients/:id/activityPlan/:activityPlanId/edit",
    name: "activityPlanEdit",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.id;
        const activityPlanName = route.query.activityPlanName;
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: "ActivityPlan",
          },
          {
            text: activityPlanName,
          },
          {
            text: "Edit",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/AddActivityPlanView.vue"
      ),
  },
  {
    path: "/patients/:id/activities/:activityId/evaluation",
    name: "addPatientActivityEvaluation",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.id;
        const evaluationName = route.query.evaluationName;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: evaluationName,
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/SubmitEvaluationView.vue"
      ),
  },
  {
    path: "/patients/:id/activities/add",
    name: "addPatientActivity",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.id;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode,
              },
            },
          },
          {
            text: "Activities",
          },
          {
            text: "Add",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "showcase" */ "../../../views/AddPatientActivityView.vue"
      ),
  },
  {
    path: "/patients/:patientCode/screening",
    name: "patient-screening",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientCode = route.params.patientCode;
        const patientId = route.query.patientId;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode || patientCode,
            to: {
              name: "singlePatient",
              params: {
                id: patientId || patientCode,
              },
              query: {
                anonymousCode: anonymousCode || patientCode,
              },
            },
          },
          {
            text: "Screening T0",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "screening" */ "../../../views/ScreeningWizardView.vue"
      ),
  },
  {
    path: "/patients/:patientId/bia/new",
    name: "bia-form",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.patientId;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode || patientId,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode: anonymousCode,
              },
            },
          },
          {
            text: "Nuovo Esame BIA",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "bia" */ "../../../components/forms/BIAForm.vue"
      ),
    props: true,
  },
  {
    path: "/patients/:patientId/bia/:activityId/edit",
    name: "bia-form-edit",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.patientId;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode || patientId,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode: anonymousCode,
              },
            },
          },
          {
            text: "Compila Esame BIA",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "bia" */ "../../../components/forms/BIAForm.vue"
      ),
    props: true,
  },
  {
    path: "/patients/:patientId/bia/:activityId/view",
    name: "bia-view",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.patientId;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode || patientId,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode: anonymousCode,
              },
            },
          },
          {
            text: "Visualizza Esame BIA",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "bia" */ "../../../views/BIAView.vue"
      ),
    props: true,
  },
  {
    path: "/patients/:patientId/nutritional-plan/:planId/view",
    name: "NutritionalPlanView",
    meta: {
      breadcrumb(route: Route) {
        const anonymousCode = route.query.anonymousCode || route.query.patientName;
        const patientId = route.params.patientId;
        const planName = route.query.planName;

        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: anonymousCode || patientId,
            to: {
              name: "singlePatient",
              params: {
                id: patientId,
              },
              query: {
                anonymousCode: anonymousCode,
              },
            },
          },
          {
            text: planName || "Piano Nutrizionale",
          },
        ];
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "nutritional-plan" */ "../../../views/NutritionalPlanView.vue"
      ),
    props: true,
  },
];

for (const patientsRoute of patientsRoutes) {
  if (!patientsRoute.meta) {
    patientsRoute.meta = {};
  }
  patientsRoute.meta.authRequired = true;
  patientsRoute.meta.userTypes = ["employee", "nutritionist"];
}
